import React, { useEffect, useMemo, useState } from "react";
import {
  Container,
  Card,
  Form,
  Table,
  Spinner,
  Alert,
  Button,
} from "react-bootstrap";

import { Search, X, Plus, Save, Pencil, Download } from "lucide-react";

import { useNavigate } from "react-router-dom";
import NavbarUtility from "./NavbarUtility";
import * as XLSX from "xlsx";

// ==========================================================
// TOKEN DI SISTEMA
// ==========================================================

const GOLD = "#ebb51b";
const GOLD_DARK = "#a67a10";
const BG_PAGE = "#121419";
const PANEL_BG = "rgba(43, 47, 56, 0.78)";
const PANEL_BORDER = "rgba(255,255,255,0.10)";
const THEAD_BG = "rgba(255,255,255,0.065)";
const THEAD_TEXT = "#e6bd4d";
const TEXT_DARK = "#f1f1ee";
const TEXT_MUTED = "#a9aaad";
const TEXT_ON_PAGE = "#f1f1ee";

// ==========================================================
// COMPONENTE
// ==========================================================

const Contatti = () => {
  const navigate = useNavigate();

  // ========================================================
  // STATE
  // ========================================================

  const [contatti, setContatti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errore, setErrore] = useState("");

  // Filtri
  const [ricerca, setRicerca] = useState("");
  const [fiera, setFiera] = useState("");
  const [nazione, setNazione] = useState("");

  const [soloClienti, setSoloClienti] = useState(false);
  const [soloFornitori, setSoloFornitori] = useState(false);
  const [soloMeccanica, setSoloMeccanica] = useState(false);
  const [soloCarrozzeria, setSoloCarrozzeria] = useState(false);
  const [soloVetro, setSoloVetro] = useState(false);

  // ========================================================
  // CARICAMENTO CONTATTI
  // ========================================================

  useEffect(() => {
    const caricaContatti = async () => {
      try {
        setLoading(true);
        setErrore("");

        const response = await fetch(
          "https://dashboard.technoparts.it/ws/contatti.php",
          { cache: "no-store" },
        );

        if (!response.ok) {
          throw new Error(`Errore HTTP ${response.status}`);
        }

        const data = await response.json();

        console.log("=================================");
        console.log("RISPOSTA API CONTATTI");
        console.log(data);
        console.log("=================================");

        const elenco = Array.isArray(data?.CONTATTI) ? data.CONTATTI : [];

        console.log("Contatti caricati:", elenco.length);

        if (elenco.length > 0) {
          console.log("PRIMO CONTATTO:", elenco[0]);
        }

        setContatti(elenco);
      } catch (error) {
        console.error("Errore caricamento contatti:", error);

        setErrore(
          "Impossibile caricare i contatti. Verifica che il servizio PHP sia raggiungibile.",
        );
      } finally {
        setLoading(false);
      }
    };

    caricaContatti();
  }, []);

  // ========================================================
  // NORMALIZZAZIONE + DEDUPLICAZIONE CONTATTI (backup frontend)
  // ========================================================

  const normalizzaTesto = (valore) =>
    String(valore ?? "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .replace(/\s+/g, " ")
      .trim();

  const punteggioFiera = (contatto) => {
    const nomeFiera = String(contatto?.fiera ?? "");
    const anni = nomeFiera.match(/(?:19|20)\d{2}/g) || [];
    const anno = anni.length ? Math.max(...anni.map(Number)) : 0;
    const idFiera = Number(contatto?.id_fiera ?? contatto?.id_fiera_ultima ?? 0) || 0;

    return { anno, idFiera };
  };

  const scegliContattoConFieraPiuRecente = (a, b) => {
    if (!a?.fiera) return b?.fiera ? b : a;
    if (!b?.fiera) return a;

    const pa = punteggioFiera(a);
    const pb = punteggioFiera(b);

    if (pb.anno !== pa.anno) return pb.anno > pa.anno ? b : a;
    if (pb.idFiera !== pa.idFiera) return pb.idFiera > pa.idFiera ? b : a;

    return a;
  };

  const contattiUnici = useMemo(() => {
    const mappa = new Map();

    contatti.forEach((contatto) => {
      const chiave =
        contatto.id_contatto != null &&
        String(contatto.id_contatto).trim() !== ""
          ? `id:${String(contatto.id_contatto).trim()}`
          : contatto.codice_cliente != null &&
              String(contatto.codice_cliente).trim() !== ""
            ? `cli:${String(contatto.codice_cliente).trim()}`
            : `nome:${normalizzaTesto(contatto.ragione_sociale)}|piva:${normalizzaTesto(
                contatto.par_iva,
              )}|indirizzo:${normalizzaTesto(contatto.indirizzo)}`;

      if (!mappa.has(chiave)) {
        mappa.set(chiave, { ...contatto, __rowKey: chiave });
        return;
      }

      // Se per errore l'API restituisce lo stesso contatto più volte,
      // manteniamo una sola riga e conserviamo i valori utili.
      const precedente = mappa.get(chiave);
      const conFieraPiuRecente = scegliContattoConFieraPiuRecente(
        precedente,
        contatto,
      );

      mappa.set(chiave, {
        ...precedente,
        ...contatto,
        id_contatto: precedente.id_contatto || contatto.id_contatto,
        codice_cliente: precedente.codice_cliente || contatto.codice_cliente,
        ragione_sociale: precedente.ragione_sociale || contatto.ragione_sociale,
        provincia: precedente.provincia || contatto.provincia,
        localita: precedente.localita || contatto.localita,
        indirizzo: precedente.indirizzo || contatto.indirizzo,
        cap: precedente.cap || contatto.cap,
        par_iva: precedente.par_iva || contatto.par_iva,
        tipo_contatto: precedente.tipo_contatto || contatto.tipo_contatto,
        nazione: precedente.nazione || contatto.nazione,
        fiera: conFieraPiuRecente?.fiera || precedente.fiera || contatto.fiera,
        id_fiera:
          conFieraPiuRecente?.id_fiera ||
          conFieraPiuRecente?.id_fiera_ultima ||
          precedente.id_fiera ||
          contatto.id_fiera,
        is_cliente:
          String(precedente.is_cliente) === "1" || String(contatto.is_cliente) === "1"
            ? "1"
            : precedente.is_cliente ?? contatto.is_cliente,
        is_fornitore:
          String(precedente.is_fornitore) === "1" ||
          String(contatto.is_fornitore) === "1"
            ? "1"
            : precedente.is_fornitore ?? contatto.is_fornitore,
        tratta_meccanica:
          String(precedente.tratta_meccanica) === "1" ||
          String(contatto.tratta_meccanica) === "1"
            ? "1"
            : precedente.tratta_meccanica ?? contatto.tratta_meccanica,
        tratta_carrozzeria:
          String(precedente.tratta_carrozzeria) === "1" ||
          String(contatto.tratta_carrozzeria) === "1"
            ? "1"
            : precedente.tratta_carrozzeria ?? contatto.tratta_carrozzeria,
        tratta_vetro:
          String(precedente.tratta_vetro) === "1" ||
          String(contatto.tratta_vetro) === "1"
            ? "1"
            : precedente.tratta_vetro ?? contatto.tratta_vetro,
        note_da_fare: Math.max(
          Number(precedente.note_da_fare || 0),
          Number(contatto.note_da_fare || 0),
        ),
        __rowKey: chiave,
      });
    });

    return Array.from(mappa.values());
  }, [contatti]);

  // ========================================================
  // ELENCO FIERE
  // ========================================================

  const fiere = useMemo(() => {
    const elenco = contattiUnici
      .map((contatto) => contatto.fiera)
      .filter(
        (valore) =>
          valore !== null &&
          valore !== undefined &&
          String(valore).trim() !== "",
      )
      .map((valore) => String(valore).trim());

    return [...new Set(elenco)].sort((a, b) =>
      a.localeCompare(b, "it", { sensitivity: "base" }),
    );
  }, [contattiUnici]);

  // ========================================================
  // ELENCO NAZIONI
  // ========================================================

  const nazioni = useMemo(() => {
    const elenco = contattiUnici
      .map((contatto) => contatto.nazione)
      .filter(
        (valore) =>
          valore !== null &&
          valore !== undefined &&
          String(valore).trim() !== "",
      )
      .map((valore) => String(valore).trim());

    return [...new Set(elenco)].sort((a, b) =>
      a.localeCompare(b, "it", { sensitivity: "base" }),
    );
  }, [contattiUnici]);

  // ========================================================
  // FILTRAGGIO
  // ========================================================

  const contattiFiltrati = useMemo(() => {
    const testo = normalizzaTesto(ricerca);
    const fieraSelezionata = normalizzaTesto(fiera);
    const nazioneSelezionata = normalizzaTesto(nazione);

    const risultati = contattiUnici.filter((contatto) => {
      // RICERCA: SOLO RAGIONE SOCIALE.
      // Scrivendo "cat ric" può passare CAT RICAMBI, non ORIENTE/AUTOTEAM.
      if (testo) {
        const nome = normalizzaTesto(contatto.ragione_sociale);

        if (!nome.includes(testo)) {
          return false;
        }
      }

      // FIERA: confronto ESATTO. Se è selezionata FRANKFURT 2026,
      // una riga senza fiera non può in alcun modo passare il filtro.
      if (fieraSelezionata) {
        const fieraContatto = normalizzaTesto(contatto.fiera);

        if (!fieraContatto || fieraContatto !== fieraSelezionata) {
          return false;
        }
      }

      // NAZIONE
      if (nazioneSelezionata) {
        const nazioneContatto = normalizzaTesto(contatto.nazione);

        if (!nazioneContatto || nazioneContatto !== nazioneSelezionata) {
          return false;
        }
      }

      if (soloClienti && String(contatto.is_cliente) !== "1") return false;
      if (soloFornitori && String(contatto.is_fornitore) !== "1") return false;
      if (soloMeccanica && String(contatto.tratta_meccanica) !== "1") return false;
      if (soloCarrozzeria && String(contatto.tratta_carrozzeria) !== "1") return false;
      if (soloVetro && String(contatto.tratta_vetro) !== "1") return false;

      return true;
    });

    return [...risultati].sort((a, b) => {
      const nomeA = normalizzaTesto(a.ragione_sociale);
      const nomeB = normalizzaTesto(b.ragione_sociale);

      if (testo) {
        const aInizia = nomeA.startsWith(testo);
        const bInizia = nomeB.startsWith(testo);

        if (aInizia && !bInizia) return -1;
        if (!aInizia && bInizia) return 1;
      }

      return nomeA.localeCompare(nomeB, "it", { sensitivity: "base" });
    });
  }, [
    contattiUnici,
    ricerca,
    fiera,
    nazione,
    soloClienti,
    soloFornitori,
    soloMeccanica,
    soloCarrozzeria,
    soloVetro,
  ]);

  // ========================================================
  // RESET FILTRI
  // ========================================================

  const resetFiltri = () => {
    setRicerca("");
    setFiera("");
    setNazione("");
    setSoloClienti(false);
    setSoloFornitori(false);
    setSoloMeccanica(false);
    setSoloCarrozzeria(false);
    setSoloVetro(false);
  };

  const filtriAttivi =
    ricerca ||
    fiera ||
    nazione ||
    soloClienti ||
    soloFornitori ||
    soloMeccanica ||
    soloCarrozzeria ||
    soloVetro;

  // ========================================================
  // // EXPORT EXCEL — SOLO RIGHE FILTRATE  // ========================================================
  const escapeExcel = (value) => {
    if (value === null || value === undefined) {
      return "";
    }
    return String(value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/\"/g, "&quot;");
  };
const scaricaExcel = () => {
  if (!contattiFiltrati || contattiFiltrati.length === 0) {
    alert("Nessun dato da esportare.");
    return;
  }

  const datiExcel = contattiFiltrati.map((contatto) => ({
    "Codice Cliente":
      contatto.codice_cliente ||
      contatto.id_cliente ||
      "",

    "Ragione Sociale":
      contatto.ragione_sociale || "",

    Provincia:
      contatto.provincia || "",

    "Località":
      contatto.localita || "",

    Indirizzo:
      contatto.indirizzo || "",

    CAP:
      contatto.cap || "",

    "P.IVA":
      contatto.par_iva || "",

    Tipo:
      contatto.tipo_contatto || "",

    Nazione:
      contatto.nazione || "",

    Fiera:
      contatto.fiera || "",

    Cliente:
      String(contatto.is_cliente) === "1"
        ? "Sì"
        : "No",

    Fornitore:
      String(contatto.is_fornitore) === "1"
        ? "Sì"
        : "No",

    Meccanica:
      String(contatto.tratta_meccanica) === "1"
        ? "Sì"
        : "No",

    Carrozzeria:
      String(contatto.tratta_carrozzeria) === "1"
        ? "Sì"
        : "No",

    Vetro:
      String(contatto.tratta_vetro) === "1"
        ? "Sì"
        : "No",

    "Note da fare": Number(contatto.note_da_fare || 0),
  }));

  const worksheet =
    XLSX.utils.json_to_sheet(datiExcel);

  worksheet["!cols"] = [
    { wch: 16 }, // Codice Cliente
    { wch: 35 }, // Ragione Sociale
    { wch: 12 }, // Provincia
    { wch: 22 }, // Località
    { wch: 35 }, // Indirizzo
    { wch: 10 }, // CAP
    { wch: 18 }, // P.IVA
    { wch: 18 }, // Tipo
    { wch: 20 }, // Nazione
    { wch: 20 }, // Fiera
    { wch: 12 }, // Cliente
    { wch: 12 }, // Fornitore
    { wch: 12 }, // Meccanica
    { wch: 14 }, // Carrozzeria
    { wch: 10 }, // Vetro
    { wch: 14 }, // Note da fare
  ];

  const workbook =
    XLSX.utils.book_new();

  XLSX.utils.book_append_sheet(
    workbook,
    worksheet,
    "Contatti"
  );

  const dataOggi = new Date()
    .toISOString()
    .split("T")[0];

  XLSX.writeFile(
    workbook,
    `contatti_filtrati_${dataOggi}.xlsx`
  );
};  // NUOVO
  // ========================================================

  const handleNuovo = () => {
    navigate("/utility/add-contatti");
  };

  const handleApriContatto = (contatto, event) => {
  if (event) {
    event.preventDefault();
    event.stopPropagation();
  }

  const idContatto = Number(contatto?.id_contatto);

  if (!idContatto) {
    console.error("ID contatto non disponibile:", contatto);
    return;
  }

  navigate(
    `/utility/add-contatti?id_contatto=${encodeURIComponent(idContatto)}`
  );
};

  // ========================================================
  // SALVA
  // ========================================================

  const handleSalva = () => {
    console.log("Salva");
  };

  // ========================================================
  // MODIFICA
  // ========================================================

  const handleModifica = () => {
    console.log("Modifica");
  };

  // ========================================================
  // APERTURA BACK ORDER
  // ========================================================

  const handleApriBackOrder = (contatto, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    console.log("=================================");
    console.log("CLICK BACK ORDER");
    console.log("CONTATTO:", contatto);

    const idCliente = contatto?.codice_cliente || contatto?.id_cliente || "";

    const ragioneSociale = contatto?.ragione_sociale || "";

    console.log("ID CLIENTE:", idCliente);
    console.log("RAGIONE SOCIALE:", ragioneSociale);

    if (!idCliente) {
      console.error(
        "ERRORE: il contatto non ha codice_cliente/id_cliente",
        contatto,
      );

      return;
    }

    const destinazione = `/utility/backorder?id_cliente=${encodeURIComponent(
      String(idCliente),
    )}&ragione_sociale=${encodeURIComponent(String(ragioneSociale))}`;

    console.log("NAVIGAZIONE VERSO:", destinazione);

    // React Router
    navigate(destinazione);

    console.log("navigate() eseguito");
  };

  // ========================================================
  // BUTTON COMPONENT
  // ========================================================

  const ActionButton = ({
    children,
    icon: Icon,
    onClick,
    variant = "outline",
  }) => (
    <button
      type="button"
      onClick={onClick}
      className={`uw-action-btn ${
        variant === "solid" ? "uw-action-solid" : ""
      }`}
    >
      <Icon size={15} />
      {children}
    </button>
  );

  // ========================================================
  // FILTER CHIP
  // ========================================================

  const FilterChip = ({ label, active, onClick }) => (
    <button
      type="button"
      onClick={onClick}
      className={`uw-chip ${active ? "is-active" : ""}`}
    >
      <span className="uw-chip-dot" />

      {label}
    </button>
  );

  // ========================================================
  // STATO DOT
  // ========================================================

  const StatoDot = ({ attivo }) => (
    <span className={`uw-status ${attivo ? "is-active" : ""}`}>
      <span className="uw-status-dot" />
      <span>{attivo ? "Sì" : "No"}</span>
    </span>
  );

  // ========================================================
  // FIERA BADGE
  // ========================================================

  const FieraBadge = ({ nome }) =>
    nome ? (
      <span className="uw-fiera-badge">{nome}</span>
    ) : (
      <span className="uw-muted">-</span>
    );

  // ========================================================
  // RENDER
  // ========================================================

  return (
    <div
      className="uw-liquid-page"
      style={{
        height: "100vh",
        backgroundColor: BG_PAGE,
        color: TEXT_ON_PAGE,
        width: "100%",
        overflow: "hidden",
      }}
    >
      {/* ==================================================
          NAVBAR
      ================================================== */}

      <NavbarUtility />

      <Container fluid className="uw-page-scroll py-4 px-3 px-md-4">
        {/* ==================================================
            FILTRI
        ================================================== */}

        <Card
          className="uw-card border-0 mb-4 overflow-visible"
          style={{
            backgroundColor: PANEL_BG,
          }}
        >
          <div className="uw-card-accent" />

          <Card.Body className="p-4">
            <div className="d-flex justify-content-between align-items-start gap-4 flex-wrap flex-lg-nowrap">
              {/* ==========================================
                  COLONNA FILTRI
              ========================================== */}

              <div
                className="flex-grow-1"
                style={{
                  minWidth: 0,
                }}
              >
                <div className="d-flex align-items-center gap-2 mb-3">
                  <span
                    style={{
                      width: "16px",
                      height: "2px",
                      backgroundColor: GOLD,
                      display: "inline-block",
                    }}
                  />

                  <h6
                    className="mb-0 fw-bold text-uppercase"
                    style={{
                      color: TEXT_DARK,
                      fontSize: "0.8rem",
                      letterSpacing: "1px",
                    }}
                  >
                    Filtri
                  </h6>

                  {filtriAttivi && (
                    <button
                      type="button"
                      onClick={resetFiltri}
                      className="uw-reset-btn d-flex align-items-center gap-1 ms-2"
                    >
                      <X size={14} />
                      Reset filtri
                    </button>
                  )}
                </div>

                <div className="row g-3">
                  {/* RICERCA */}

                  <div className="col-12 col-xl-3">
                    <Form.Label
                      className="fw-semibold"
                      style={{
                        color: TEXT_DARK,
                        fontSize: "0.85rem",
                      }}
                    >
                      Ricerca
                    </Form.Label>

                    <div className="position-relative">
                      <Search
                        size={17}
                        className="position-absolute"
                        style={{
                          left: "12px",
                          top: "11px",
                          color: TEXT_MUTED,
                        }}
                      />

                      <Form.Control
                        type="text"
                        placeholder="Cerca per ragione sociale..."
                        value={ricerca}
                        onChange={(e) => setRicerca(e.target.value)}
                        className="uw-input"
                        style={{
                          paddingLeft: "40px",
                        }}
                      />
                    </div>
                  </div>

                  {/* FIERA */}

                  <div className="col-12 col-xl-4">
                    <Form.Label
                      className="fw-semibold"
                      style={{
                        color: TEXT_DARK,
                        fontSize: "0.85rem",
                      }}
                    >
                      Fiera
                    </Form.Label>

                    <Form.Select
  value={fiera}
  onChange={(e) => setFiera(e.target.value)}
  className="uw-input"
>
  <option value="">TUTTE LE FIERE</option>

  {fiere.map((nomeFiera) => (
    <option key={nomeFiera} value={nomeFiera}>
      {String(nomeFiera).toUpperCase()}
    </option>
  ))}
</Form.Select>
                  </div>

                  {/* NAZIONE */}

                  <div className="col-12 col-xl-3">
                    <Form.Label
                      className="fw-semibold"
                      style={{
                        color: TEXT_DARK,
                        fontSize: "0.85rem",
                      }}
                    >
                      Nazione
                    </Form.Label>

                    <Form.Select
                      value={nazione}
                      onChange={(e) => setNazione(e.target.value)}
                      className="uw-input"
                    >
                      <option value="">Tutte le nazioni</option>

                      {nazioni.map((nomeNazione) => (
                        <option key={nomeNazione} value={nomeNazione}>
                          {nomeNazione}
                        </option>
                      ))}
                    </Form.Select>
                  </div>

                  {/* RISULTATI */}

                  <div className="col-12 col-xl-2 d-flex align-items-end">
                    <div className="uw-results-box w-100 d-flex align-items-center justify-content-between px-3 py-2">
                      <span
                        style={{
                          fontSize: "0.85rem",
                          color: TEXT_DARK,
                        }}
                      >
                        Risultati
                      </span>

                      <span
                        style={{
                          fontSize: "0.95rem",
                          color: "#e6bd4d",
                          fontWeight: 700,
                        }}
                      >
                        {contattiFiltrati.length}

                        <span
                          style={{
                            color: TEXT_MUTED,
                            fontWeight: 500,
                          }}
                        >
                          {" "}
                          / {contattiUnici.length}
                        </span>
                      </span>
                    </div>
                  </div>
                </div>

                <hr
                  style={{
                    borderColor: PANEL_BORDER,
                  }}
                />

                <div className="d-flex flex-wrap gap-2">
                  <FilterChip
                    label="Solo clienti"
                    active={soloClienti}
                    onClick={() => setSoloClienti((v) => !v)}
                  />

                  <FilterChip
                    label="Solo fornitori"
                    active={soloFornitori}
                    onClick={() => setSoloFornitori((v) => !v)}
                  />

                  <FilterChip
                    label="Tratta meccanica"
                    active={soloMeccanica}
                    onClick={() => setSoloMeccanica((v) => !v)}
                  />

                  <FilterChip
                    label="Tratta carrozzeria"
                    active={soloCarrozzeria}
                    onClick={() => setSoloCarrozzeria((v) => !v)}
                  />

                  <FilterChip
                    label="Tratta vetro"
                    active={soloVetro}
                    onClick={() => setSoloVetro((v) => !v)}
                  />
                </div>
              </div>

              {/* ==========================================
                  AZIONI
              ========================================== */}

              <div
                className="d-flex flex-column justify-content-center gap-2 flex-shrink-0"
                style={{
                  width: "170px",
                }}
              >
                <ActionButton icon={Plus} onClick={handleNuovo} variant="solid">
                  Nuovo Contatto
                </ActionButton>

                <ActionButton icon={Download} onClick={scaricaExcel}>
                  Scarica Excel
                </ActionButton>
              </div>
            </div>
          </Card.Body>
        </Card>

        {/* ==================================================
            LOADING
        ================================================== */}

        {loading && (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{
              minHeight: "300px",
            }}
          >
            <Spinner
              animation="border"
              style={{
                color: GOLD,
              }}
            />
          </div>
        )}

        {/* ==================================================
            ERRORE
        ================================================== */}

        {!loading && errore && (
          <Alert variant="danger" className="uw-glass-alert border-0">
            {errore}
          </Alert>
        )}

        {/* ==================================================
            TABELLA
        ================================================== */}

        {!loading && !errore && (
          <Card
            className="uw-card border-0 overflow-hidden"
            style={{
              backgroundColor: PANEL_BG,
            }}
          >
            <div
              className="scroll-hidden uw-table-shell"
              style={{
                overflowX: "auto",
                maxHeight: "calc(100vh - 380px)",
              }}
            >
              <Table hover responsive className="mb-0 align-middle uw-table">
                <thead
                  style={{
                    position: "sticky",
                    top: 0,
                    zIndex: 2,
                    backgroundColor: THEAD_BG,
                    color: THEAD_TEXT,
                  }}
                >
                  <tr>
                    <th>Codice Cliente</th>

                    <th>Ragione Sociale</th>

                    <th>Provincia</th>

                    <th>Località</th>

                    <th>Indirizzo</th>

                    <th>CAP</th>

                    <th>P.IVA</th>

                    <th>Tipo</th>

                    <th>Nazione</th>

                    <th>Fiera</th>

                    <th>Cliente</th>

                    <th>Fornitore</th>

                    <th>Meccanica</th>

                    <th>Carrozzeria</th>

                    <th>Vetro</th>

                    <th className="text-center">Note</th>
                  </tr>
                </thead>

                <tbody>
                  {contattiFiltrati.length === 0 ? (
                    <tr>
                      <td
                        colSpan="16"
                        className="text-center py-5"
                        style={{
                          color: TEXT_MUTED,
                        }}
                      >
                        Nessun contatto trovato
                      </td>
                    </tr>
                  ) : (
                    contattiFiltrati.map((contatto, index) => (
                      <tr
                        key={
                          contatto.__rowKey ||
                          contatto.id_contatto ||
                          `${contatto.codice_cliente || "no-code"}-${contatto.ragione_sociale || "no-name"}-${index}`
                        }
                      >
                        {/* CODICE CLIENTE */}

                        <td>
                          {contatto.codice_cliente ||
                            contatto.id_cliente ||
                            "-"}
                        </td>

                        {/* =================================
                              RAGIONE SOCIALE
                          ================================= */}

                        <td>
                          <button
                            type="button"
                            className="ragione-sociale-button"
                            onClick={(event) =>
  handleApriContatto(contatto, event)
}
title="Apri contatto"
                          >
                            <strong>{contatto.ragione_sociale || "-"}</strong>
                          </button>
                        </td>

                        {/* PROVINCIA */}

                        <td>{contatto.provincia || "-"}</td>

                        {/* LOCALITÀ */}

                        <td>{contatto.localita || "-"}</td>

                        {/* INDIRIZZO */}

                        <td>{contatto.indirizzo || "-"}</td>

                        {/* CAP */}

                        <td>{contatto.cap || "-"}</td>

                        {/* P.IVA */}

                        <td>{contatto.par_iva || "-"}</td>

                        {/* TIPO */}

                        <td>{contatto.tipo_contatto || "-"}</td>

                        {/* NAZIONE */}

                        <td>{contatto.nazione || "-"}</td>

                        {/* FIERA */}

                        <td>
                          <FieraBadge nome={contatto.fiera} />
                        </td>

                        {/* CLIENTE */}

                        <td>
                          <StatoDot
                            attivo={String(contatto.is_cliente) === "1"}
                          />
                        </td>

                        {/* FORNITORE */}

                        <td>
                          <StatoDot
                            attivo={String(contatto.is_fornitore) === "1"}
                          />
                        </td>

                        {/* MECCANICA */}

                        <td>
                          <StatoDot
                            attivo={String(contatto.tratta_meccanica) === "1"}
                          />
                        </td>

                        {/* CARROZZERIA */}

                        <td>
                          <StatoDot
                            attivo={String(contatto.tratta_carrozzeria) === "1"}
                          />
                        </td>

                        {/* VETRO */}

                        <td>
                          <StatoDot
                            attivo={String(contatto.tratta_vetro) === "1"}
                          />
                        </td>

                        <td className="text-center">
  <span
    className={`uw-note-count ${
      Number(contatto.note_da_fare || 0) > 0
        ? "has-notes"
        : ""
    }`}
    title={`${Number(contatto.note_da_fare || 0)} note da fare`}
  >
    {Number(contatto.note_da_fare || 0)}
  </span>
</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>
          </Card>
        )}
      </Container>

      {/* ======================================================
          STYLE
      ====================================================== */}

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

        html,
        body,
        #root {
          width: 100%;
          height: 100%;
          margin: 0;
          background: #121419 !important;
          overflow: hidden;
        }

        body,
        .container-fluid {
          font-family: 'Inter', sans-serif;
        }

        .uw-liquid-page {
          --uw-gold: #ebb51b;
          --uw-gold-dark: #a67a10;
          --uw-white: #f7f7f4;
          --uw-text: #f1f1ee;
          --uw-muted: #a9aaad;
          --uw-muted-2: #81838a;
          --uw-glass-border: rgba(255,255,255,.105);
          --uw-shadow: 0 22px 54px rgba(0,0,0,.38);

          color: var(--uw-text);
          background:
            radial-gradient(circle at 12% 8%, rgba(235,181,27,.10), transparent 28%),
            radial-gradient(circle at 88% 12%, rgba(255,255,255,.045), transparent 27%),
            radial-gradient(circle at 65% 86%, rgba(166,122,16,.055), transparent 34%),
            linear-gradient(145deg, #0d0f13 0%, #1c2027 50%, #0b0d11 100%) !important;
        }

        .uw-liquid-page > nav,
        .uw-liquid-page > .navbar,
        .uw-liquid-page .navbar {
          position: relative;
          z-index: 1000;
          background: rgba(17,20,25,.78) !important;
          backdrop-filter: blur(24px) saturate(145%);
          -webkit-backdrop-filter: blur(24px) saturate(145%);
          border-bottom: 1px solid rgba(235,181,27,.22) !important;
          box-shadow:
            0 12px 34px rgba(0,0,0,.30),
            inset 0 -1px 0 rgba(255,255,255,.035);
          color: var(--uw-white) !important;
        }

        .uw-liquid-page .navbar * {
          color: inherit;
        }

        .uw-liquid-page .uw-page-scroll {
          position: relative;
          height: calc(100vh - 60px);
          min-height: 0;
          overflow-y: auto;
          overflow-x: hidden;
          background: transparent;
          padding-bottom: 56px !important;
          scroll-padding-bottom: 56px;
        }

        .uw-card {
          position: relative;
          background:
            radial-gradient(circle at 12% 0%, rgba(255,255,255,.065), transparent 31%),
            linear-gradient(145deg, rgba(58,63,74,.86), rgba(37,41,49,.78)) !important;
          border: 1px solid var(--uw-glass-border) !important;
          border-radius: 24px !important;
          box-shadow:
            var(--uw-shadow),
            inset 0 1px 0 rgba(255,255,255,.075),
            inset 0 -1px 0 rgba(0,0,0,.30) !important;
          backdrop-filter: blur(22px) saturate(140%);
          -webkit-backdrop-filter: blur(22px) saturate(140%);
          color: var(--uw-text);
        }

        .uw-card::after {
          content: "";
          position: absolute;
          inset: 0;
          pointer-events: none;
          border-radius: inherit;
          background:
            linear-gradient(
              115deg,
              rgba(255,255,255,.042),
              transparent 24%,
              transparent 73%,
              rgba(235,181,27,.024)
            );
        }

        .uw-card > * {
          position: relative;
          z-index: 1;
        }

        .uw-card-accent {
          height: 2px;
          width: 100%;
          background:
            linear-gradient(
              90deg,
              transparent,
              rgba(235,181,27,.38) 15%,
              #ebb51b 50%,
              rgba(166,122,16,.70) 82%,
              transparent
            );
          box-shadow: 0 0 18px rgba(235,181,27,.20);
        }

        .uw-reset-btn {
          background: transparent;
          border: 0;
          color: var(--uw-muted);
          font-size: .78rem;
          font-weight: 600;
          padding: 4px 6px;
          cursor: pointer;
          transition: color .15s ease;
        }

        .uw-reset-btn:hover {
          color: #f0c84d;
        }

        .uw-input,
        .uw-input.form-select {
          min-height: 40px;
          border: 1px solid rgba(255,255,255,.115) !important;
          border-radius: 12px !important;
          background:
            linear-gradient(
              145deg,
              rgba(255,255,255,.085),
              rgba(255,255,255,.030)
            ) !important;
          color: var(--uw-white) !important;
          -webkit-text-fill-color: var(--uw-white);
          box-shadow:
            inset 0 1px 0 rgba(255,255,255,.060),
            0 4px 14px rgba(0,0,0,.15);
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
        }

        .uw-input::placeholder {
          color: #85888f !important;
          -webkit-text-fill-color: #85888f;
          opacity: 1;
        }

        .uw-input:focus {
          border-color: rgba(235,181,27,.68) !important;
          box-shadow:
            0 0 0 3px rgba(235,181,27,.11),
            0 8px 22px rgba(0,0,0,.20) !important;
          outline: none !important;
        }

        select.uw-input option {
          background: #292d35;
          color: #f4f4f1;
        }

        .uw-results-box {
          min-height: 40px;
          border: 1px solid rgba(235,181,27,.26);
          border-radius: 12px;
          background:
            linear-gradient(
              145deg,
              rgba(235,181,27,.11),
              rgba(255,255,255,.035)
            );
          box-shadow: inset 0 1px 0 rgba(255,255,255,.045);
        }

        .uw-chip {
          display: inline-flex;
          align-items: center;
          gap: 7px;
          border: 1px solid rgba(255,255,255,.105);
          border-radius: 999px;
          background:
            linear-gradient(
              145deg,
              rgba(255,255,255,.055),
              rgba(255,255,255,.018)
            );
          color: var(--uw-muted);
          font-size: .78rem;
          font-weight: 600;
          padding: 7px 13px;
          cursor: pointer;
          box-shadow: inset 0 1px 0 rgba(255,255,255,.035);
          transition: all .16s ease;
        }

        .uw-chip:hover {
          border-color: rgba(235,181,27,.35);
          color: #e3e3df;
        }

        .uw-chip.is-active {
          border-color: rgba(235,181,27,.62);
          background:
            linear-gradient(
              145deg,
              rgba(235,181,27,.18),
              rgba(235,181,27,.065)
            );
          color: #f0cb60;
          box-shadow:
            inset 0 1px 0 rgba(255,255,255,.055),
            0 6px 16px rgba(0,0,0,.14);
        }

        .uw-chip-dot {
          width: 7px;
          height: 7px;
          border-radius: 50%;
          display: inline-block;
          background: #6f7279;
          box-shadow: 0 0 0 3px rgba(255,255,255,.025);
        }

        .uw-chip.is-active .uw-chip-dot {
          background: #ebb51b;
          box-shadow: 0 0 10px rgba(235,181,27,.42);
        }

        .uw-action-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          width: 100%;
          border: 1px solid rgba(235,181,27,.38);
          border-radius: 12px;
          background:
            linear-gradient(
              145deg,
              rgba(255,255,255,.075),
              rgba(255,255,255,.022)
            );
          color: #e6bd4d;
          font-size: .80rem;
          font-weight: 700;
          padding: 9px 12px;
          cursor: pointer;
          box-shadow:
            inset 0 1px 0 rgba(255,255,255,.050),
            0 7px 18px rgba(0,0,0,.16);
          transition: all .16s ease;
        }

        .uw-action-btn:hover {
          transform: translateY(-1px);
          border-color: rgba(235,181,27,.62);
          background:
            linear-gradient(
              145deg,
              rgba(235,181,27,.15),
              rgba(255,255,255,.050)
            );
          color: #fff;
        }

        .uw-action-solid {
          background:
            linear-gradient(145deg, #f0c43e, #a67a10) !important;
          color: #17191e !important;
          border-color: rgba(235,181,27,.75) !important;
          box-shadow:
            0 9px 22px rgba(0,0,0,.23),
            inset 0 1px 0 rgba(255,255,255,.28);
        }

        .uw-action-solid:hover {
          background:
            linear-gradient(145deg, #f5cf57, #b88812) !important;
          color: #121419 !important;
        }

        .uw-table-shell {
          border: 1px solid rgba(255,255,255,.08);
          border-radius: 20px;
          overflow: auto !important;
          box-shadow:
            0 14px 34px rgba(0,0,0,.23),
            inset 0 1px 0 rgba(255,255,255,.040);
          backdrop-filter: blur(14px);
          -webkit-backdrop-filter: blur(14px);
        }

        .uw-table {
          --bs-table-bg: transparent;
          --bs-table-color: var(--uw-text);
          --bs-table-hover-bg: transparent;
          margin: 0;
          background: transparent !important;
        }

        .uw-table thead th {
          position: sticky;
          top: 0;
          z-index: 4;
          background: rgba(32,36,43,.96) !important;
          color: #d7b84f !important;
          border-color: rgba(255,255,255,.075) !important;
          font-size: .70rem;
          text-transform: uppercase;
          letter-spacing: .55px;
          font-weight: 700;
          padding: 12px 14px;
          white-space: nowrap;
          backdrop-filter: blur(14px);
          -webkit-backdrop-filter: blur(14px);
        }

        .uw-table tbody td {
          font-size: .82rem;
          padding: 10px 14px;
          color: #ecece8 !important;
          border-color: rgba(255,255,255,.055) !important;
          background: rgba(255,255,255,.030) !important;
        }

        .uw-table tbody tr:nth-child(even) td {
          background: rgba(255,255,255,.043) !important;
        }

        .uw-table tbody tr:hover td {
          background: rgba(235,181,27,.072) !important;
        }

        .ragione-sociale-button {
          border: 0;
          background: transparent;
          padding: 0;
          margin: 0;
          color: var(--uw-white);
          cursor: pointer;
          text-align: left;
          font-family: inherit;
          font-size: inherit;
          width: 100%;
        }

        .ragione-sociale-button strong {
          color: #f5f5f2 !important;
          transition: color .12s ease;
        }

        .ragione-sociale-button:hover strong {
          color: #efc95b !important;
          text-decoration: underline;
        }

        .ragione-sociale-button:focus {
          outline: none;
        }

        .ragione-sociale-button:focus-visible {
          outline: 2px solid var(--uw-gold);
          outline-offset: 3px;
          border-radius: 4px;
        }

        .uw-status {
          display: inline-flex;
          align-items: center;
          gap: 7px;
          color: #777a82;
          font-weight: 500;
        }

        .uw-status-dot {
          width: 7px;
          height: 7px;
          border-radius: 50%;
          flex-shrink: 0;
          background: #62656d;
        }

        .uw-status.is-active {
          color: #ecece8;
          font-weight: 650;
        }

        .uw-status.is-active .uw-status-dot {
          background: #47bd73;
          box-shadow: 0 0 10px rgba(71,189,115,.32);
        }

        .uw-fiera-badge {
          display: inline-block;
          border: 1px solid rgba(235,181,27,.42);
          border-radius: 999px;
          background: rgba(235,181,27,.075);
          color: #e8c65d;
          font-size: .70rem;
          font-weight: 650;
          padding: 3px 9px;
          white-space: nowrap;
        }

        .uw-muted {
          color: var(--uw-muted);
        }

        .uw-glass-alert {
          border: 1px solid rgba(211,81,81,.28) !important;
          border-left: 3px solid rgba(211,81,81,.78) !important;
          border-radius: 16px !important;
          background:
            linear-gradient(
              145deg,
              rgba(150,45,45,.20),
              rgba(255,255,255,.030)
            ) !important;
          color: #f0c4c4 !important;
          box-shadow:
            0 12px 28px rgba(0,0,0,.20),
            inset 0 1px 0 rgba(255,255,255,.045);
          backdrop-filter: blur(14px);
          -webkit-backdrop-filter: blur(14px);
        }

        .scroll-hidden {
          scrollbar-width: none;
          -ms-overflow-style: none;
        }

        .scroll-hidden::-webkit-scrollbar {
          display: none;
        }

        @media (max-width: 991.98px) {
          .uw-card {
            border-radius: 18px !important;
          }

          .uw-page-scroll {
            height: calc(100vh - 56px) !important;
          }
        }

        select.uw-input {
  text-transform: uppercase;
}

select.uw-input option {
  text-transform: uppercase;
}


.uw-note-count {
  min-width: 27px;
  height: 27px;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  padding: 0 7px;

  border-radius: 999px;

  color: #7f838b;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);

  font-size: 11px;
  font-weight: 800;

  transition: all 0.15s ease;
}

.uw-note-count.has-notes {
  color: #ffd45a;

  background: rgba(235, 181, 27, 0.13);

  border-color: rgba(235, 181, 27, 0.46);

  box-shadow:
    0 0 12px rgba(235, 181, 27, 0.12),
    inset 0 1px 0 rgba(255, 255, 255, 0.07);
}
      `}</style>
    </div>
  );
};

export default Contatti;
